import React, { useState, useRef, useEffect } from "react";
//mui
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
  DialogActions,
  Typography,
} from "@mui/material";
//狀態管理
import { observer } from "mobx-react";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
//預覽word
import { renderAsync } from "docx-preview";
//預覽excel
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { transformExcelToFortune } from "@corbe30/fortune-excel";
//icons
import CloseIcon from "@mui/icons-material/Close";
//api
import { dataEntryAPI } from "../../API/dataEntryAPI";
//翻譯
import { useTranslation } from "react-i18next";
import { getSafeErrorMessage } from "../../../services/toast";

// 獨立的 ExcelPreview 組件（使用 fortune-sheet）
const ExcelPreview = ({ link }) => {
  const sheetRef = useRef();
  const containerRef = useRef(null);
  const [key, setKey] = useState(0);
  const [sheets, setSheets] = useState([{ name: "Sheet1" }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 在 capture 階段攔截 wheel 事件，完全取代 fortune-sheet 內部的 wheel handler。
  // 原因：fortune-sheet 的 handleGlobalWheel 在 Dialog 環境下向上 scroll 有問題，
  // 改用 capture 阻止它，直接操作 .luckysheet-scrollbar-y DOM element，
  // fortune-sheet 的 onScroll handler 會正常接手更新 ctx.scrollTop 與重繪 canvas。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation(); // capture 階段呼叫：阻止事件繼續往子層傳遞，fortune-sheet 的 bubble listener 不會觸發

      const scrollbarY = container.querySelector(".luckysheet-scrollbar-y");
      if (!scrollbarY) return;

      // 依 deltaMode 換算實際 pixel 量
      let delta;
      switch (e.deltaMode) {
        case 1: // lines
          delta = e.deltaY * 30;
          break;
        case 2: // pages
          delta = e.deltaY * 400;
          break;
        default: // pixels
          delta = e.deltaY;
      }

      scrollbarY.scrollTop += delta;
    };

    container.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });
    return () =>
      container.removeEventListener("wheel", handleWheel, { capture: true });
  }, []);

  useEffect(() => {
    if (!link) return;

    const fetchAndParse = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(link);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const fileName = link.split("/").pop() || "file.xlsx";
        const file = new File([blob], fileName, { type: blob.type });

        await transformExcelToFortune(file, setSheets, setKey, sheetRef);
      } catch (err) {
        console.error("Excel 預覽出錯:", err);
        setError(getSafeErrorMessage());
      } finally {
        setLoading(false);
      }
    };

    fetchAndParse();
  }, [link]);

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", height: "500px", position: "relative" }}
    >
      {/* 載入中 overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.85)",
          }}
        >
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1 }}>載入 Excel 中...</Typography>
        </Box>
      )}
      {/* 錯誤 overlay */}
      {error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {/* Workbook 保持常駐，確保 sheetRef 在 transformExcelToFortune 執行時有效 */}
      <Workbook
        key={key}
        ref={sheetRef}
        data={sheets}
        allowEdit={false}
        showToolbar={false}
        showFormulaBar={false}
      />
    </Box>
  );
};

// 獨立的 WordPreview 組件
const WordPreview = ({ link }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAndRenderDocument = async () => {
      if (!link) return;

      setLoading(true);
      setError(null);

      try {
        // 呼叫 API 層的方法取得 ArrayBuffer
        const arrayBuffer = await dataEntryAPI.PreviewWordFileAPI(link);

        if (!isMounted) return;

        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          await renderAsync(arrayBuffer, containerRef.current, null, {
            className: "docx-viewer",
            inWrapper: true,
          });
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("文檔預覽出錯:", err);
        setError(getSafeErrorMessage());
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAndRenderDocument();

    return () => {
      isMounted = false;
    };
  }, [link]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "500px",
        padding: 2,
        overflow: "auto",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CircularProgress size={24} />
          <Typography>載入文檔中...</Typography>
        </Box>
      )}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          transform: "scale(0.95)",
          transformOrigin: "top left",
          width: "fit-content",
          // 修正：使用 opacity 與 position，解決 display:none 導致 docx-preview 找不到寬高的渲染破圖問題
          opacity: loading || error ? 0 : 1,
          position: loading || error ? "absolute" : "relative",
        }}
      >
        <div ref={containerRef} />
      </Box>
    </Box>
  );
};

//main
const DataEntryPreviewModal = observer(() => {
  const { t } = useTranslation("ActivityMain"); //之後要改
  const store = emissionViewStore;

  // 統一的關閉事件處理，減少程式碼重複
  const handleClose = () => {
    emissionViewStore._handleCloseDataEntryPreviewModal();
  };

  const selectedFile = emissionViewStore.selectedValidationFile;

  // 判斷不同檔案類型，渲染預覽畫面
  const renderFilePreview = (extension, fileLink) => {
    if (!extension || !fileLink) return null;

    const ext = extension.toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

    if (ext === "pdf") {
      return (
        <object
          data={fileLink}
          type="application/pdf"
          width="100%"
          height="500px" // 設定為固定高度
        >
          <Typography>{t("Unable to preview PDF file.")}</Typography>
        </object>
      );
    }

    if (ext === "docx") {
      return (
        <Box>
          <WordPreview link={fileLink} />
        </Box>
      );
    }

    if (ext === "xlsx" || ext === "xls") {
      return <ExcelPreview link={fileLink} />;
    }

    if (imageExtensions.includes(ext)) {
      return (
        <Box
          component="img"
          src={fileLink}
          alt={t("Preview Image")}
          sx={{
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />
      );
    }

    return (
      <Typography sx={{ p: 2 }}>
        {t("This file format does not support preview functionality")}.
      </Typography>
    );
  };

  // 確保這時候有檔案資料，避免 MobX 狀態清空時 Modal 還在動畫階段導致當機
  if (!selectedFile) return null;

  return (
    <Dialog
      open={emissionViewStore.showDataEntryPreviewModal}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {t("Preview File For")} {selectedFile.fileName}.{selectedFile.extension}
      </DialogTitle>
      <Divider />
      <DialogContent>
        {renderFilePreview(selectedFile.extension, selectedFile.path)}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DataEntryPreviewModal;
